import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EventNoteIcon from '@mui/icons-material/EventNote';
import config from '../../../config';

const STATUS_COLOR = {
  Pending:  { bg: '#fef3c7', color: '#92400e' },
  Approved: { bg: '#dcfce7', color: '#166534' },
  Rejected: { bg: '#fee2e2', color: '#991b1b' },
  Cancelled:{ bg: '#f1f5f9', color: '#475569' },
};

// HR Leave Requests + Leave Balance allocation. Approving a leave
// (server-side) deducts from balance and stamps Leave attendance rows
// for each day in the range.
export default function LeaveRequests() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;

  const [tab, setTab] = useState(0);

  // shared reference data
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  // ── Requests ────────────────────────────────────────────────────
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [empFilter, setEmpFilter] = useState('');
  const [loading, setLoading] = useState(false);

  // Apply dialog
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyForm, setApplyForm] = useState({
    employeeId: '',
    leaveTypeId: '',
    fromDate: new Date().toISOString().slice(0, 10),
    toDate: new Date().toISOString().slice(0, 10),
    reason: '',
  });

  // Approve dialog
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveRemark, setApproveRemark] = useState('');
  const [approveAction, setApproveAction] = useState('approve'); // approve / reject

  // ── Balance tab ─────────────────────────────────────────────────
  const [balances, setBalances] = useState([]);
  const [balLoading, setBalLoading] = useState(false);
  const [balEmpId, setBalEmpId] = useState('');
  const [balYear, setBalYear] = useState(new Date().getFullYear());

  // Allocate dialog
  const [allocOpen, setAllocOpen] = useState(false);
  const [allocForm, setAllocForm] = useState({
    employeeId: '',
    leaveTypeId: '',
    year: new Date().getFullYear(),
    allocated: 0,
  });

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // ── Loaders ──────────────────────────────────────────────────────
  const loadReference = async () => {
    const [eRes, tRes] = await Promise.all([
      fetch(`${config.API_BASE_URL}/Hr/Employees/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch(`${config.API_BASE_URL}/Hr/LeaveTypes/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    if (eRes.ok) setEmployees(await eRes.json());
    if (tRes.ok) setLeaveTypes(await tRes.json());
  };

  const loadRequests = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.append('status', statusFilter);
      if (empFilter) qs.append('employeeId', empFilter);
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/LeaveRequests/${companyId}?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setRequests(await res.json());
    } catch (e) {
      toast('Failed to load requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadBalances = async () => {
    if (!companyId) return;
    setBalLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.append('year', balYear);
      if (balEmpId) qs.append('employeeId', balEmpId);
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/LeaveBalances/${companyId}?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setBalances(await res.json());
    } catch (e) {
      toast('Failed to load balances', 'error');
    } finally {
      setBalLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) loadReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (tab === 0) loadRequests();
    else loadBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, statusFilter, empFilter, balEmpId, balYear, companyId]);

  // ── Actions ──────────────────────────────────────────────────────
  const submitApply = async () => {
    if (!applyForm.employeeId || !applyForm.leaveTypeId) {
      toast('Pick employee and leave type', 'warning');
      return;
    }
    if (applyForm.toDate < applyForm.fromDate) {
      toast('End date can\'t be before start', 'warning');
      return;
    }
    const fromD = new Date(applyForm.fromDate);
    const toD = new Date(applyForm.toDate);
    const days =
      Math.floor((toD - fromD) / (24 * 60 * 60 * 1000)) + 1;
    const body = {
      Id: null,
      CompanyId: companyId,
      EmployeeId: Number(applyForm.employeeId),
      LeaveTypeId: Number(applyForm.leaveTypeId),
      FromDate: applyForm.fromDate,
      ToDate: applyForm.toDate,
      Days: days,
      Reason: applyForm.reason || null,
      UserId: user?.id || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/LeaveRequest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast(data.message || 'Failed', 'error');
      return;
    }
    toast('Leave request submitted');
    setApplyOpen(false);
    loadRequests();
  };

  const submitApprove = async () => {
    if (!approveTarget) return;
    const body = {
      Id: approveTarget.id,
      CompanyId: companyId,
      Approve: approveAction === 'approve',
      Remark: approveRemark || null,
      UserId: user?.id || null,
    };
    const res = await fetch(
      `${config.API_BASE_URL}/Hr/LeaveRequest/Approve`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      },
    );
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast(data.message || 'Failed', 'error');
      return;
    }
    toast(approveAction === 'approve' ? 'Leave approved' : 'Leave rejected');
    setApproveOpen(false);
    loadRequests();
  };

  const submitAllocate = async () => {
    if (!allocForm.employeeId || !allocForm.leaveTypeId) {
      toast('Pick employee and leave type', 'warning');
      return;
    }
    const body = {
      CompanyId: companyId,
      EmployeeId: Number(allocForm.employeeId),
      LeaveTypeId: Number(allocForm.leaveTypeId),
      Year: Number(allocForm.year),
      Allocated: Number(allocForm.allocated) || 0,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/LeaveBalance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast('Failed', 'error');
      return;
    }
    toast('Allocation saved');
    setAllocOpen(false);
    loadBalances();
  };

  // ── Render helpers ───────────────────────────────────────────────
  const empName = (id) =>
    employees.find((e) => e.id === id)?.employeeName || `#${id}`;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: '#2563eb',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <EventNoteIcon />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
            Leaves
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
            HR · Requests + Balance allocation
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label='Leave Requests' />
          <Tab label='Leave Balance' />
        </Tabs>
      </Card>

      {tab === 0 && (
        <>
          {/* Filter bar + apply */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mb: 2 }}>
            <TextField
              select
              size='small'
              label='Status'
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ bgcolor: '#fff', minWidth: 160 }}>
              <MenuItem value=''>All</MenuItem>
              <MenuItem value='Pending'>Pending</MenuItem>
              <MenuItem value='Approved'>Approved</MenuItem>
              <MenuItem value='Rejected'>Rejected</MenuItem>
            </TextField>
            <TextField
              select
              size='small'
              label='Employee'
              value={empFilter}
              onChange={(e) => setEmpFilter(e.target.value)}
              sx={{ bgcolor: '#fff', minWidth: 220 }}>
              <MenuItem value=''>All</MenuItem>
              {employees.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.employeeName} ({e.employeeCode})
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => setApplyOpen(true)}
              sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}>
              Apply Leave
            </Button>
          </Stack>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>To</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Days</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align='right'>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align='center' sx={{ py: 4 }}>
                            <Typography sx={{ color: '#94a3b8' }}>
                              No leave requests.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {requests.map((r) => {
                        const sc = STATUS_COLOR[r.status] || STATUS_COLOR.Pending;
                        return (
                          <TableRow key={r.id} hover>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {r.employeeName}
                              <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                                {r.employeeCode}
                              </Typography>
                            </TableCell>
                            <TableCell>{r.leaveTypeName}</TableCell>
                            <TableCell>
                              {new Date(r.fromDate).toLocaleDateString('en-IN')}
                            </TableCell>
                            <TableCell>
                              {new Date(r.toDate).toLocaleDateString('en-IN')}
                            </TableCell>
                            <TableCell>{r.days}</TableCell>
                            <TableCell sx={{ color: '#475569' }}>
                              {r.reason || '—'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size='small'
                                label={r.status}
                                sx={{
                                  bgcolor: sc.bg,
                                  color: sc.color,
                                  fontWeight: 600,
                                }}
                              />
                              {r.approverRemark && (
                                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', mt: 0.4 }}>
                                  {r.approverRemark}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align='right'>
                              {r.status === 'Pending' && (
                                <>
                                  <Tooltip title='Approve'>
                                    <IconButton
                                      size='small'
                                      onClick={() => {
                                        setApproveTarget(r);
                                        setApproveAction('approve');
                                        setApproveRemark('');
                                        setApproveOpen(true);
                                      }}
                                      sx={{ color: '#16a34a' }}>
                                      <CheckCircleIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title='Reject'>
                                    <IconButton
                                      size='small'
                                      onClick={() => {
                                        setApproveTarget(r);
                                        setApproveAction('reject');
                                        setApproveRemark('');
                                        setApproveOpen(true);
                                      }}
                                      sx={{ color: '#dc2626' }}>
                                      <CancelIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {tab === 1 && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mb: 2 }}>
            <TextField
              select
              size='small'
              label='Employee'
              value={balEmpId}
              onChange={(e) => setBalEmpId(e.target.value)}
              sx={{ bgcolor: '#fff', minWidth: 220 }}>
              <MenuItem value=''>All</MenuItem>
              {employees.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.employeeName} ({e.employeeCode})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type='number'
              size='small'
              label='Year'
              value={balYear}
              onChange={(e) => setBalYear(Number(e.target.value))}
              sx={{ bgcolor: '#fff', width: 110 }}
            />
            <Box sx={{ flex: 1 }} />
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => {
                setAllocForm({
                  employeeId: balEmpId || '',
                  leaveTypeId: '',
                  year: balYear,
                  allocated: 0,
                });
                setAllocOpen(true);
              }}
              sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}>
              Allocate Leave
            </Button>
          </Stack>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {balLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Leave Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Year</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Allocated</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Used</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {balances.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align='center' sx={{ py: 4 }}>
                            <Typography sx={{ color: '#94a3b8' }}>
                              No allocations.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {balances.map((b) => (
                        <TableRow key={b.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {empName(b.employeeId)}
                          </TableCell>
                          <TableCell>{b.leaveTypeName}</TableCell>
                          <TableCell>{b.year}</TableCell>
                          <TableCell>{b.allocated}</TableCell>
                          <TableCell>{b.used}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {b.balance}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Apply dialog */}
      <Dialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Apply Leave</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                size='small'
                fullWidth
                label='Employee *'
                value={applyForm.employeeId}
                onChange={(e) =>
                  setApplyForm({ ...applyForm, employeeId: e.target.value })
                }>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.employeeName} ({e.employeeCode})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                size='small'
                fullWidth
                label='Leave Type *'
                value={applyForm.leaveTypeId}
                onChange={(e) =>
                  setApplyForm({ ...applyForm, leaveTypeId: e.target.value })
                }>
                {leaveTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.leaveTypeName} ({t.code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='date'
                size='small'
                fullWidth
                label='From *'
                InputLabelProps={{ shrink: true }}
                value={applyForm.fromDate}
                onChange={(e) =>
                  setApplyForm({ ...applyForm, fromDate: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='date'
                size='small'
                fullWidth
                label='To *'
                InputLabelProps={{ shrink: true }}
                value={applyForm.toDate}
                onChange={(e) =>
                  setApplyForm({ ...applyForm, toDate: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                fullWidth
                multiline
                rows={2}
                label='Reason'
                value={applyForm.reason}
                onChange={(e) =>
                  setApplyForm({ ...applyForm, reason: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setApplyOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={submitApply}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve / reject dialog */}
      <Dialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        maxWidth='xs'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {approveAction === 'approve' ? 'Approve Leave' : 'Reject Leave'}
        </DialogTitle>
        <DialogContent dividers>
          {approveTarget && (
            <>
              <Typography sx={{ fontSize: '0.85rem', mb: 1 }}>
                {approveTarget.employeeName} · {approveTarget.leaveTypeName} ·{' '}
                {approveTarget.days} day(s)
              </Typography>
              <TextField
                size='small'
                fullWidth
                multiline
                rows={2}
                label='Remark'
                value={approveRemark}
                onChange={(e) => setApproveRemark(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={submitApprove}
            sx={{
              bgcolor: approveAction === 'approve' ? '#16a34a' : '#dc2626',
              '&:hover': {
                bgcolor: approveAction === 'approve' ? '#15803d' : '#b91c1c',
              },
            }}>
            {approveAction === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Allocate balance dialog */}
      <Dialog
        open={allocOpen}
        onClose={() => setAllocOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Allocate Leave Balance</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                size='small'
                fullWidth
                label='Employee *'
                value={allocForm.employeeId}
                onChange={(e) =>
                  setAllocForm({ ...allocForm, employeeId: e.target.value })
                }>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.employeeName} ({e.employeeCode})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                size='small'
                fullWidth
                label='Leave Type *'
                value={allocForm.leaveTypeId}
                onChange={(e) =>
                  setAllocForm({ ...allocForm, leaveTypeId: e.target.value })
                }>
                {leaveTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.leaveTypeName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Year'
                value={allocForm.year}
                onChange={(e) =>
                  setAllocForm({ ...allocForm, year: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Allocated Days'
                value={allocForm.allocated}
                onChange={(e) =>
                  setAllocForm({ ...allocForm, allocated: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAllocOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={submitAllocate}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
